import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Car, CheckCircle2, CreditCard, MapPin } from "lucide-react";
import { toast } from "sonner";
import { DEPOSIT, PACKAGES, TIME_SLOTS } from "../data";
import { api, formatApiError, useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";

const stepIcons = [Car, CalendarDays, MapPin, CreditCard];
const stepLabels = ["Package", "Schedule", "Location", "Confirm"];

export default function Booking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialPackage = params.get("package");

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    package_id: PACKAGES.some((p) => p.id === initialPackage) ? initialPackage : "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    date: "",
    time_slot: "",
    address: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
    payment_option: "pay_on_day",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
    }
  }, [user]);

  const pkg = useMemo(() => PACKAGES.find((p) => p.id === form.package_id), [form.package_id]);
  const today = new Date().toISOString().split("T")[0];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = [
    !!form.package_id,
    !!(form.vehicle_make && form.vehicle_model && form.vehicle_year && form.date && form.time_slot),
    form.address.trim().length >= 5 && !!form.name.trim() && /.+@.+\..+/.test(form.email) && form.phone.trim().length >= 7,
    true,
  ][step];

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { ...form, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setDone(data.booking);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 pt-16">
        <Reveal className="gold-border-card p-10 max-w-lg text-center" data-testid="booking-confirmation">
          <CheckCircle2 className="text-amber-400 mx-auto" size={44} />
          <h1 className="font-display font-bold uppercase text-2xl tracking-tight mt-6">Detail Reserved</h1>
          <p className="text-zinc-400 text-sm mt-4 leading-relaxed">
            {done.package_name} on <span className="text-amber-300">{done.date}</span> at <span className="text-amber-300">{done.time_slot}</span>.
            A confirmation email and text message are on their way to <span className="text-amber-300">{done.customer_email}</span> and your phone. Our mobile studio will meet you at your address — pay the remaining balance on the day.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {user ? (
              <button onClick={() => navigate("/account")} data-testid="booking-view-account-button" className="gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full">
                My Bookings
              </button>
            ) : (
              <Link to="/register" data-testid="booking-create-account-link" className="gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full">
                Create an Account to Track Bookings
              </Link>
            )}
            <button onClick={() => navigate("/")} data-testid="booking-back-home-button" className="border border-amber-500/40 text-amber-200 font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full">
              Home
            </button>
          </div>
        </Reveal>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto" data-testid="booking-page">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-400/90 font-mono-gold mb-3">Reserve Your Detail — No Account Needed</p>
        <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl">
          Book <span className="gold-text">mobile service</span>
        </h1>
      </Reveal>

      <div className="mt-10 flex items-center gap-2" data-testid="booking-steps-indicator">
        {stepLabels.map((label, i) => {
          const Icon = stepIcons[i];
          return (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? "text-amber-300" : "text-zinc-600"}`}>
                <span className={`w-8 h-8 rounded-full border flex items-center justify-center ${i <= step ? "border-amber-400/60 bg-amber-500/10" : "border-zinc-700"}`}>
                  <Icon size={14} />
                </span>
                <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em]">{label}</span>
              </div>
              {i < stepLabels.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-amber-500/50" : "bg-zinc-800"}`} />}
            </div>
          );
        })}
      </div>

      <div className="mt-10 gold-border-card p-6 sm:p-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
            {step === 0 && (
              <div className="space-y-4" data-testid="booking-step-package">
                {PACKAGES.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => set("package_id", p.id)}
                    data-testid={`booking-package-${p.id}`}
                    className={`w-full text-left p-5 rounded-lg border transition-all duration-200 flex items-center justify-between gap-4 ${
                      form.package_id === p.id ? "border-amber-400/70 bg-amber-500/10" : "border-zinc-800 hover:border-amber-500/30"
                    }`}
                  >
                    <div>
                      <p className="font-display font-semibold uppercase tracking-tight">{p.name}</p>
                      <p className="text-zinc-500 text-xs mt-1">{p.tagline}</p>
                    </div>
                    <span className="font-display font-extrabold gold-text text-2xl whitespace-nowrap">${p.priceMin}–{p.priceMax}</span>
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="booking-step-schedule">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <input className="vpd-input" placeholder="Make (e.g. Porsche)" value={form.vehicle_make} onChange={(e) => set("vehicle_make", e.target.value)} data-testid="booking-vehicle-make" />
                  <input className="vpd-input" placeholder="Model (e.g. 911)" value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} data-testid="booking-vehicle-model" />
                  <input className="vpd-input" placeholder="Year" value={form.vehicle_year} onChange={(e) => set("vehicle_year", e.target.value)} data-testid="booking-vehicle-year" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-zinc-500 block mb-2">Service date</label>
                  <input type="date" min={today} className="vpd-input" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="booking-date-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-zinc-500 block mb-2">Arrival window</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {TIME_SLOTS.map((t) => (
                      <button
                        key={t}
                        onClick={() => set("time_slot", t)}
                        data-testid={`booking-slot-${t.replace(/[\s:]/g, "-")}`}
                        className={`py-3 rounded-lg border text-xs font-mono-gold tracking-widest transition-all ${
                          form.time_slot === t ? "border-amber-400/70 bg-amber-500/10 text-amber-200" : "border-zinc-800 text-zinc-400 hover:border-amber-500/30"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="booking-step-location">
                <div>
                  <label className="text-xs uppercase tracking-[0.25em] text-zinc-500 block mb-2">Your address — we come to you</label>
                  <textarea className="vpd-input min-h-[90px]" placeholder="Full street address where the vehicle is parked — your driveway, office car park, hangar… This is where our mobile studio will meet you." value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="booking-address-input" />
                  <p className="text-amber-400/80 text-xs flex items-center gap-2 mt-2"><MapPin size={13} /> Our fully-equipped mobile studio arrives at this address — no drop-off needed.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input className="vpd-input" placeholder="Your name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name-input" />
                  <input className="vpd-input" type="email" placeholder="Email for confirmation" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email-input" />
                </div>
                <input className="vpd-input" type="tel" placeholder="Contact phone — for your confirmation text" required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone-input" />
                <textarea className="vpd-input min-h-[80px]" placeholder="Notes — pet hair, stains, gate code, anything we should know (optional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="booking-notes-input" />
              </div>
            )}

            {step === 3 && pkg && (
              <div data-testid="booking-step-confirm">
                <div className="rounded-lg border border-zinc-800 p-5 space-y-2 text-sm">
                  <p className="flex justify-between"><span className="text-zinc-500">Package</span><span className="text-zinc-200">{pkg.name}</span></p>
                  <p className="flex justify-between"><span className="text-zinc-500">Vehicle</span><span className="text-zinc-200">{form.vehicle_year} {form.vehicle_make} {form.vehicle_model}</span></p>
                  <p className="flex justify-between"><span className="text-zinc-500">When</span><span className="text-zinc-200">{form.date} · {form.time_slot}</span></p>
                  <p className="flex justify-between"><span className="text-zinc-500">Where</span><span className="text-zinc-200 text-right max-w-[60%]">{form.address}</span></p>
                  <p className="flex justify-between"><span className="text-zinc-500">Contact</span><span className="text-zinc-200">{form.name} · {form.email}</span></p>
                  <p className="flex justify-between border-t border-zinc-800 pt-2 mt-2"><span className="text-zinc-500">Estimated total</span><span className="gold-text font-display font-bold text-lg">${pkg.priceMin}–{pkg.priceMax}</span></p>
                </div>
                <div className="mt-6 space-y-3" data-testid="booking-payment-options">
                  <button
                    onClick={() => set("payment_option", "deposit")}
                    data-testid="booking-pay-deposit-option"
                    className={`w-full text-left p-5 rounded-lg border transition-all ${form.payment_option === "deposit" ? "border-amber-400/70 bg-amber-500/10" : "border-zinc-800 hover:border-amber-500/30"}`}
                  >
                    <p className="font-semibold text-sm text-zinc-100">Secure with ${DEPOSIT} deposit</p>
                    <p className="text-zinc-500 text-xs mt-1">Pay the deposit now by card via Stripe. Remainder on the day.</p>
                  </button>
                  <button
                    onClick={() => set("payment_option", "pay_on_day")}
                    data-testid="booking-pay-on-day-option"
                    className={`w-full text-left p-5 rounded-lg border transition-all ${form.payment_option === "pay_on_day" ? "border-amber-400/70 bg-amber-500/10" : "border-zinc-800 hover:border-amber-500/30"}`}
                  >
                    <p className="font-semibold text-sm text-zinc-100">Pay on the day</p>
                    <p className="text-zinc-500 text-xs mt-1">No payment now. Settle the full amount when the detail is complete.</p>
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="booking-back-button"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-amber-300 disabled:opacity-30 transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < 3 ? (
            <button
              onClick={() => canNext && setStep((s) => s + 1)}
              disabled={!canNext}
              data-testid="booking-next-button"
              className="gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3.5 rounded-full disabled:opacity-40 flex items-center gap-2 hover:scale-[1.02] transition-transform"
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="booking-submit-button"
              className="gold-foil text-black font-semibold uppercase tracking-[0.2em] text-xs px-8 py-3.5 rounded-full disabled:opacity-40 hover:scale-[1.02] transition-transform"
            >
              {submitting ? "Reserving…" : form.payment_option === "deposit" ? `Pay $${DEPOSIT} Deposit` : "Confirm Booking"}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
